import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../../design-system/components/Button';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { animations } from '../../design-system/tokens/animations';
import { shadows } from '../../design-system/tokens/shadows';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const UploadArea = styled.div<{ isDragging: boolean }>`
  border: ${borders.width.base} dashed ${props => props.isDragging ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: ${borders.radius.lg};
  padding: ${spacing[8]};
  text-align: center;
  cursor: pointer;
  transition: ${animations.transition(['border-color', 'background-color', 'transform'], animations.duration.normal, animations.easing.easeOut)};
  background: ${props => props.isDragging ? props.theme.colors.overlayLight : props.theme.colors.surface};
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.overlayLight};
    transform: translateY(-2px);
    box-shadow: ${props => shadows.colored.sm(props.theme.colors.primary)};
  }
`;

const Input = styled.input`
  display: none;
`;

const Icon = styled.div`
  font-size: ${typography.fontSize['5xl']};
  margin-bottom: ${spacing[3]};
  opacity: 0.6;
  transition: ${animations.transition(['opacity', 'transform'], animations.duration.normal, animations.easing.easeOut)};
  
  ${UploadArea}:hover & {
    opacity: 0.8;
    transform: scale(1.1);
  }
`;

const Text = styled.p`
  color: ${props => props.theme.colors.text};
  margin: 0 0 ${spacing[2]} 0;
  font-weight: ${typography.fontWeight.semibold};
  font-size: ${typography.fontSize.base};
  font-family: ${typography.fontFamily.primary};
`;

const Hint = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
  font-size: ${typography.fontSize.xs};
  font-family: ${typography.fontFamily.primary};
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing[3]};
  background: ${props => props.theme.colors.surfaceHover};
  border-radius: ${borders.radius.lg};
  border: ${borders.width.thin} solid ${props => props.theme.colors.border};
  transition: ${animations.transition(['border-color', 'box-shadow'], animations.duration.fast, animations.easing.easeOut)};
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => shadows.colored.sm(props.theme.colors.primary)};
  }
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.div`
  color: ${props => props.theme.colors.text};
  font-weight: ${typography.fontWeight.medium};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileSize = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${typography.fontSize.xs};
  font-family: ${typography.fontFamily.primary};
  margin-top: ${spacing[1]};
`;

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  onFileRemove?: () => void;
  accept?: string;
  maxSize?: number; // in bytes
  currentFile?: File | null;
  label?: string;
  hint?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  onFileRemove,
  accept,
  maxSize,
  currentFile,
  label,
  hint,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const resolvedLabel: string = label ?? ((t('common.uploadFile') as string) || 'Upload File');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handleFileChange = (file: File) => {
    if (maxSize && file.size > maxSize) {
      alert(
        t('common.fileSizeExceedsMaximum', { size: formatFileSize(maxSize) })
      );
      return;
    }
    onFileSelect(file);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileChange(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  return (
    <Container>
      <UploadArea
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        isDragging={isDragging}
      >
        <Icon>📎</Icon>
        <Text>{resolvedLabel}</Text>
        {hint && <Hint>{hint}</Hint>}
        {maxSize && (
          <Hint>
            {t('common.maxFileSize', { size: formatFileSize(maxSize) })}
          </Hint>
        )}
      </UploadArea>
      <Input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            handleFileChange(file);
          }
        }}
      />
      {currentFile && (
        <FileList>
          <FileItem>
            <FileInfo>
              <FileName>{currentFile.name}</FileName>
              <FileSize>{formatFileSize(currentFile.size)}</FileSize>
            </FileInfo>
            {onFileRemove && (
              <Button
                size="sm"
                variant="outline"
                onClick={onFileRemove}
                aria-label={t('common.removeFileNamed', { name: currentFile.name })}
                style={{
                  borderColor: theme.colors.error,
                  color: theme.colors.error,
                  minWidth: 'auto'
                }}
              >
                {t('common.remove')}
              </Button>
            )}
          </FileItem>
        </FileList>
      )}
    </Container>
  );
};

export default FileUpload;
